/**
 * Audio utility functions for WebM/Opus handling
 */

export interface AudioFormatSupport {
  webmOpus: boolean;
  webmAudio: boolean;
  preferredMimeType: string | null;
}

/**
 * Check browser support for WebM/Opus formats
 */
export const checkAudioFormatSupport = (): AudioFormatSupport => {
  console.log('Checking audio format support...');
  
  // Check if MediaRecorder is available
  if (typeof MediaRecorder === 'undefined') {
    console.warn('MediaRecorder is not available in this browser');
    return {
      webmOpus: false,
      webmAudio: false,
      preferredMimeType: null
    };
  }
  
  const webmOpus = MediaRecorder.isTypeSupported('video/webm;codecs=opus');
  const webmAudio = MediaRecorder.isTypeSupported('audio/webm;codecs=opus');
  
  console.log('Format support check results:');
  console.log('- video/webm;codecs=opus:', webmOpus);
  console.log('- audio/webm;codecs=opus:', webmAudio);
  
  let preferredMimeType: string | null = null;
  
  if (webmAudio) {
    preferredMimeType = 'audio/webm;codecs=opus';
  } else if (webmOpus) {
    preferredMimeType = 'video/webm;codecs=opus';
  }
  
  console.log('Preferred MIME type:', preferredMimeType);
  
  return {
    webmOpus,
    webmAudio,
    preferredMimeType
  };
};

/**
 * Get optimal MediaRecorder configuration for WebM/Opus
 */
export const getOptimalRecorderConfig = (): MediaRecorderOptions => {
  const support = checkAudioFormatSupport();
  
  if (!support.preferredMimeType) {
    throw new Error('WebM/Opus format not supported');
  }
  
  return {
    mimeType: support.preferredMimeType,
    audioBitsPerSecond: 48000, // 48kbps - lower bitrate for more predictable chunk sizes
    bitsPerSecond: 48000, // Fallback for older browsers
  };
};

/**
 * Get optimal audio constraints for microphone access
 */
export const getOptimalAudioConstraints = (): MediaTrackConstraints => {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48000, // Optimal for Opus codec
    channelCount: 1, // Mono for efficiency
  };
};

/**
 * Convert Blob to ArrayBuffer for network transmission
 */
export const blobToArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to ArrayBuffer'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Validate audio chunk data
 */
export const validateAudioChunk = (blob: Blob): boolean => {
  // Check if blob has data
  if (!blob || blob.size === 0) {
    return false;
  }
  
  // Check if it's a WebM format (allow both webm and empty type for initial chunks)
  const isWebM = blob.type.includes('webm') || blob.type === '';
  if (!isWebM) {
    console.warn('Audio chunk is not WebM format:', blob.type);
    return false;
  }
  
  // Check reasonable size limits (more flexible for small initial chunks)
  const minSize = 100; // 100 bytes (reduced from 1KB for initial chunks)
  const maxSize = 2 * 1024 * 1024; // 2MB (increased for longer chunks)
  
  if (blob.size < minSize) {
    console.warn('Audio chunk too small:', blob.size);
    return false;
  }
  
  if (blob.size > maxSize) {
    console.warn('Audio chunk too large:', blob.size);
    return false;
  }
  
  return true;
};

/**
 * Calculate estimated bitrate from chunk data
 */
export const calculateBitrate = (chunkSize: number, durationMs: number): number => {
  if (durationMs <= 0) return 0;
  
  const bitsPerSecond = (chunkSize * 8) / (durationMs / 1000);
  return Math.round(bitsPerSecond);
};

/**
 * Combine multiple small audio blobs into a larger one for streaming
 */
export const combineAudioBlobs = (blobs: Blob[], mimeType: string): Blob => {
  return new Blob(blobs, { type: mimeType });
};

/**
 * Check if chunk should be buffered based on size
 */
export const shouldBufferChunk = (blob: Blob, minBufferSize: number = 1024): boolean => {
  return blob.size < minBufferSize;
};

/**
 * Format error messages for audio capture issues
 */
export const formatAudioError = (error: unknown): string => {
  if (error instanceof Error) {
    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'Microphone access denied. Please allow microphone permissions and try again.';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'No microphone device found. Please connect a microphone and try again.';
      case 'NotReadableError':
        return 'Microphone is already in use by another application.';
      case 'OverconstrainedError':
        return 'Microphone does not support the required audio settings.';
      case 'SecurityError':
        return 'Microphone access blocked due to security restrictions.';
      default:
        return `Audio error: ${error.message}`;
    }
  }
  
  return 'Unknown audio error occurred';
};